import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Grid, Button, Chip } from '@mui/material'
import { amber } from '@mui/material/colors'
import TableTemplate from '../../Common/Table/TableTemplate'
import { setDateFormat, setTimeFormat } from '../../../utils/dateUtils'
import { fontSizes, fontWeights } from '../../Common/Typography/TypographyStyles'
import { Task } from '../../../redux/task/manageTasks/types'

import TaskBadge from '../../Task/TaskComponents/TaskTableComponents/TaskBadge'

// import ViewAssignedTeamMembers from './ViewAssignedTeamMembers'
import { useAppSelector } from '../../../redux/store'
import {
  selectFilteredTasksIsNotArchived,
  selectListOfTasksReport,
  selectTasksLoading,
  selectTasksError,
} from '../../../redux/task/manageTasks/selector'

interface Column {
  field: string
  headerName: string
  headerAlign?: 'left' | 'right' | 'center'
  bodyAlignment?: 'center' | 'right' | 'left'
  width?: string | number
  renderCell?: (value: any, item: any) => React.ReactNode
  renderFormat?: (value: any) => React.ReactNode
  sortable: boolean
}

interface TaskTableProps {
  onViewDetails?: () => void
}

const TaskTable: React.FC<TaskTableProps> = ({ onViewDetails }) => {
  const navigate = useNavigate()
  const taskData = useAppSelector(selectFilteredTasksIsNotArchived)
  const listOfTasksReport = useAppSelector(selectListOfTasksReport)
  const loading = useAppSelector(selectTasksLoading)
  const error = useAppSelector(selectTasksError)

  // Build a map from taskId (string) -> compliance rate
  const complianceRateMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const reportTask of listOfTasksReport) {
      const total =
        reportTask.inProgressTeamMembers +
        reportTask.completedTeamMembers +
        reportTask.notStartedTeamMembers
      const rate = total > 0 ? parseFloat(((reportTask.completedTeamMembers / total) * 100).toFixed(2)) : 0
      map.set(reportTask._id, rate)
    }
    return map
  }, [listOfTasksReport])

  const columns = useMemo<Column[]>(
    () => [
      {
        field: 'title',
        headerName: 'TITLE',
        bodyAlignment: 'center',
        sortable: true,
        renderCell: (param: string) => (
          <Typography
            sx={{
              maxWidth: 200,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              margin: '0 auto',
              fontSize: fontSizes.size12,
              fontWeight: fontWeights[700],
            }}
          >
            {param}
          </Typography>
        ),
      },

      {
        field: 'createdAt',
        headerName: 'CREATED DATE',
        bodyAlignment: 'center',
        sortable: true,
        renderFormat: (value: string | null) => (
          <Grid container spacing={0} direction='column' alignContent='center'>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  fontWeight: fontWeights[700],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {value ? setDateFormat(value) : '—'}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        field: 'dueDate',
        headerName: 'DUE DATE',
        bodyAlignment: 'center',
        sortable: true,
        renderFormat: (value: Date | null) => (
          <Grid container spacing={0} direction='column' alignContent='center'>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  fontWeight: fontWeights[700],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {value ? setDateFormat(value.toString()) : 'No due date'}
              </Typography>
            </Grid>
            {value && (
              <Grid>
                <Typography
                  sx={{
                    fontSize: fontSizes.size12,
                    fontWeight: fontWeights[400],
                    lineHeight: 'ratio9to8',
                    letterSpacing: 'loose',
                  }}
                >
                  {setTimeFormat(value.toString())}
                </Typography>
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        field: 'importance',
        headerName: 'IMPORTANCE',
        bodyAlignment: 'center',
        sortable: true,
        width: '20%',
        renderFormat: (value: string) => 
        <Grid container spacing={0} direction='column' alignContent='center'>
          <TaskBadge value={value} badgeType='importanceBadge' />
        </Grid>  ,
      },
      {
        field: 'id',
        headerName: 'COMPLIANCE RATE',
        bodyAlignment: 'center',
        sortable: false,
        renderCell: (id: number, _row: Task) => {
          const rate = complianceRateMap.get(String(id))
          const displayRate = rate !== undefined ? rate : null
          const chipColor =
            displayRate === null
              ? 'default'
              : displayRate >= 80
              ? 'success'
              : displayRate >= 50
              ? 'warning'
              : 'error'
          const isAmber = displayRate !== null && displayRate >= 50 && displayRate < 80
          return (
            <Chip
              label={displayRate !== null ? `${displayRate.toFixed(2)}%` : '—'}
              color={chipColor as any}
              variant='outlined'
              size='small'
              sx={{
                fontWeight: fontWeights[700],
                fontSize: fontSizes.size12,
                minWidth: 72,
                ...(isAmber && { borderColor: amber[700], color: amber[700] }),
              }}
            />
          )
        },
      },
      {
        field: 'id',
        headerName: 'ACTION',
        bodyAlignment: 'center',
        sortable: false,
        renderCell: (_id: number, row: Task) => (
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={() => {
              onViewDetails?.()
              navigate(`/reports/task/${row._id}`)
            }}
          >
            View Details
          </Button>
        ),
      },
    ],
    [complianceRateMap, navigate, onViewDetails],
  )

  return (
    <React.Fragment>
      <TableTemplate columns={columns} datas={taskData} state={[loading, error]} uniqueField='id' />
    </React.Fragment>
  )
}

export default TaskTable
